/**
 * M8 sunucu durumu — TanStack Query.
 *
 * Neden burada toplanıyor: gösterge, mutabakat ve terminal ekranları AYNI
 * uçları farklı tazelik ihtiyaçlarıyla kullanır. Tazelik kararını ekrana
 * bırakırsak iki ekran aynı ucu iki farklı aralığı ile döverek KK-539/KK-540'in
 * korumaya çalıştığı şeyi bozar: yönetim panelinin açık unutulmuş sekmesi
 * teneffüs pikinde kasayı yavaşlatamaz.
 *
 * Bu yüzden:
 *   · gösterge/terminal gibi CANLI ekranlar 60 sn'de bir tazelenir,
 *   · rapor uçları yalnızca parametre değişince çekilir (refetchInterval yok),
 *   · sekme arka plandayken yenileme YAPILMAZ (refetchIntervalInBackground yok).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiOku, apiYaz } from "./api.js";
import type {
  AnomaliYaniti,
  AuditYaniti,
  FotoKapsama,
  KarZararRaporu,
  Maruziyet,
  MutabakatKarari,
  MutabakatYaniti,
  NoktaKarsilastirmaRaporu,
  NoktaOzeti,
  NoktaSorumlulukRaporu,
  OkulOzeti,
  Ozet,
  TerminalSaglikYaniti,
  TokenYenilemeYaniti,
  UrunKarliligiRaporu,
} from "./tipler.js";
import type { ZRaporu } from "@kantin/shared";

/** Canlı ekranların yenileme aralığı (ms). */
const CANLI_ARALIK = 60_000;

// ---------------------------------------------------------------------------
// Kabuk
// ---------------------------------------------------------------------------

export function useOkul() {
  return useQuery({
    queryKey: ["m8", "okul"],
    queryFn: () => apiOku<OkulOzeti>("/okul"),
    staleTime: 300_000,
  });
}

export function useNoktalar() {
  return useQuery({
    queryKey: ["m8", "nokta"],
    queryFn: () => apiOku<{ noktalar: NoktaOzeti[] }>("/nokta"),
    staleTime: 300_000,
  });
}

// ---------------------------------------------------------------------------
// Gösterge — KK-802 / KK-803 / KK-810 / KK-811 / KK-538
// ---------------------------------------------------------------------------

export function useOzet() {
  return useQuery({
    queryKey: ["m8", "ozet"],
    queryFn: () => apiOku<Ozet>("/yonetim/ozet"),
    refetchInterval: CANLI_ARALIK,
    staleTime: 30_000,
  });
}

export function useMaruziyet() {
  return useQuery({
    queryKey: ["m8", "maruziyet"],
    queryFn: () => apiOku<Maruziyet>("/yonetim/offline-maruziyet"),
    refetchInterval: CANLI_ARALIK,
    staleTime: 30_000,
  });
}

export function useFotoKapsama() {
  return useQuery({
    queryKey: ["m8", "foto-kapsama"],
    queryFn: () => apiOku<FotoKapsama>("/yonetim/foto-kapsama"),
    staleTime: 600_000,
  });
}

/**
 * KK-810: sepet ortalaması ve "en çok satan ürün" TEK sorguda üretilir.
 * Gösterge bunları kendisi hesaplamaz — iki ayrı uçtan çekilip ekranda
 * birleştirilseydi noktalar farklı anların fotoğrafını gösterirdi.
 */
export function useNoktaKarsilastirma(
  bas: string | undefined,
  bit: string | undefined,
  noktaId: string | null,
) {
  return useQuery({
    queryKey: ["m8", "nokta-karsilastirma", bas ?? "", bit ?? "", noktaId],
    queryFn: () =>
      apiOku<NoktaKarsilastirmaRaporu>("/rapor/nokta-karsilastirma", {
        bas,
        bit,
        nokta_id: noktaId ?? undefined,
      }),
    enabled: Boolean(bas && bit),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutabakat — KK-805
// ---------------------------------------------------------------------------

export function useMutabakat(cozuldu: boolean | null, sayfa: number) {
  return useQuery({
    queryKey: ["m8", "mutabakat", cozuldu, sayfa],
    queryFn: () =>
      apiOku<MutabakatYaniti>("/mutabakat", {
        cozuldu: cozuldu === null ? undefined : cozuldu,
        sayfa,
      }),
    staleTime: 15_000,
  });
}

export function useMutabakatCoz() {
  const istemci = useQueryClient();
  return useMutation({
    mutationFn: (girdi: { id: string; karar: MutabakatKarari; not?: string }) =>
      apiYaz<{ id: string; karar: string; cozuldu: boolean }>(
        "POST",
        `/mutabakat/${girdi.id}/coz`,
        girdi.not
          ? { karar: girdi.karar, not: girdi.not }
          : { karar: girdi.karar },
      ),
    onSuccess: () => {
      // Çözüm satışı iptal edebilir; özet (bekleyen sayacı, ciro) de değişir.
      void istemci.invalidateQueries({ queryKey: ["m8", "mutabakat"] });
      void istemci.invalidateQueries({ queryKey: ["m8", "ozet"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Terminal sağlığı — KK-804 / KK-410 / AK-12 / GV-02
// ---------------------------------------------------------------------------

export function useTerminalSagligi() {
  return useQuery({
    queryKey: ["m8", "terminal-saglik"],
    queryFn: () => apiOku<TerminalSaglikYaniti>("/terminal/saglik"),
    refetchInterval: CANLI_ARALIK,
    staleTime: 30_000,
  });
}

export function useTokenYenile() {
  const istemci = useQueryClient();
  return useMutation({
    mutationFn: (terminalId: number) =>
      apiYaz<TokenYenilemeYaniti>(
        "POST",
        `/terminal/${terminalId}/token-yenile`,
      ),
    onSuccess: () => {
      void istemci.invalidateQueries({ queryKey: ["m8", "terminal-saglik"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Anomali — KK-806
// ---------------------------------------------------------------------------

export function useAnomali(
  incelendi: boolean | null,
  siddet: number | null,
  sayfa: number,
) {
  return useQuery({
    queryKey: ["m8", "anomali", incelendi, siddet, sayfa],
    queryFn: () =>
      apiOku<AnomaliYaniti>("/anomali", {
        incelendi: incelendi === null ? undefined : incelendi,
        siddet: siddet ?? undefined,
        sayfa,
      }),
    staleTime: 30_000,
  });
}

export function useAnomaliIsaretle() {
  const istemci = useQueryClient();
  return useMutation({
    mutationFn: (girdi: { id: string; incelendi: boolean }) =>
      apiYaz<{ id: string; incelendi: boolean }>(
        "POST",
        `/anomali/${girdi.id}/incelendi`,
        { incelendi: girdi.incelendi },
      ),
    onSuccess: () => {
      void istemci.invalidateQueries({ queryKey: ["m8", "anomali"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Denetim kaydı — KK-807 (SALT OKUNUR)
// ---------------------------------------------------------------------------

export interface AuditFiltresi {
  eylem?: string;
  aktor_tipi?: string;
  varlik?: string;
  bas?: string;
  bit?: string;
}

export function useAudit(filtre: AuditFiltresi, sayfa: number) {
  return useQuery({
    queryKey: ["m8", "audit", filtre, sayfa],
    queryFn: () =>
      apiOku<AuditYaniti>("/audit", {
        eylem: filtre.eylem || undefined,
        aktor_tipi: filtre.aktor_tipi || undefined,
        varlik: filtre.varlik || undefined,
        bas: filtre.bas || undefined,
        bit: filtre.bit || undefined,
        sayfa,
      }),
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------------------
// Raporlar
// ---------------------------------------------------------------------------

export function useZRaporu(tarih: string, noktaId: string | null) {
  return useQuery({
    queryKey: ["m8", "z-raporu", tarih, noktaId],
    queryFn: () =>
      apiOku<ZRaporu>(`/rapor/z/${tarih}`, {
        nokta_id: noktaId ?? undefined,
      }),
    enabled: Boolean(tarih),
    staleTime: 60_000,
  });
}

export function useKarZarar(bas: string, bit: string, noktaId: string | null) {
  return useQuery({
    queryKey: ["m8", "kar-zarar", bas, bit, noktaId],
    queryFn: () =>
      apiOku<KarZararRaporu>("/rapor/kar-zarar", {
        bas,
        bit,
        nokta_id: noktaId ?? undefined,
      }),
    enabled: Boolean(bas && bit),
    staleTime: 60_000,
  });
}

export function useUrunKarlilik(
  bas: string,
  bit: string,
  noktaId: string | null,
  limit: number,
) {
  return useQuery({
    queryKey: ["m8", "urun-karlilik", bas, bit, noktaId, limit],
    queryFn: () =>
      apiOku<UrunKarliligiRaporu>("/rapor/urun-karlilik", {
        bas,
        bit,
        nokta_id: noktaId ?? undefined,
        limit,
      }),
    enabled: Boolean(bas && bit),
    staleTime: 60_000,
  });
}

export function useNoktaSorumluluk(
  bas: string,
  bit: string,
  noktaId: string | null,
) {
  return useQuery({
    queryKey: ["m8", "nokta-sorumluluk", bas, bit, noktaId],
    queryFn: () =>
      apiOku<NoktaSorumlulukRaporu>("/rapor/nokta-sorumluluk", {
        bas,
        bit,
        nokta_id: noktaId ?? undefined,
      }),
    enabled: Boolean(bas && bit),
    staleTime: 60_000,
  });
}
